/**
 * STEP — Auth API
 * Typed wrappers around all authentication endpoints.
 */
import api from "@/lib/axios";
import type { AuthUser, TenantInfo } from "@/store/authStore";

// ── SMS-OTP auth (apps.identity) ──────────────────────────────────────────────
export type OtpPurpose = "login" | "register" | "reset" | "add_phone";

export interface OtpMembership {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  role: string;
}

export interface OtpRequestResult {
  sent: boolean;
  phone: string;
  purpose: string;
  resend_in: number;
  dev_code?: string; // only when backend OTP_RETURN_CODE/DEBUG (dev/staging)
}

export interface OtpVerifyResult {
  ticket: string;
  phone: string;
  purpose: string;
  has_account: boolean;
  memberships: OtpMembership[];
}

export interface OtpSelectResult {
  access: string;
  refresh: string;
  user: AuthUser;
  tenant?: TenantInfo;
}

/** POST /api/v1/auth/otp/request/ */
export async function otpRequest(phone: string, purpose: OtpPurpose = "login"): Promise<OtpRequestResult> {
  const res = await api.post<{ success: boolean; data: OtpRequestResult }>("/auth/otp/request/", { phone, purpose });
  return res.data.data;
}

/** POST /api/v1/auth/otp/verify/ */
export async function otpVerify(phone: string, code: string, purpose: OtpPurpose = "login"): Promise<OtpVerifyResult> {
  const res = await api.post<{ success: boolean; data: OtpVerifyResult }>("/auth/otp/verify/", { phone, code, purpose });
  return res.data.data;
}

/** POST /api/v1/auth/otp/select/ */
export async function otpSelect(ticket: string, tenantId: string): Promise<OtpSelectResult> {
  const res = await api.post<{ success: boolean; data: OtpSelectResult }>("/auth/otp/select/", { ticket, tenant_id: tenantId });
  return res.data.data;
}

/** POST /api/v1/auth/phone/request/ — send a code to a logged-in user adding a phone. */
export async function addPhoneRequest(phone: string): Promise<{ sent: boolean; resend_in: number; dev_code?: string }> {
  const res = await api.post<{ success: boolean; data: { sent: boolean; resend_in: number; dev_code?: string } }>(
    "/auth/phone/request/", { phone }
  );
  return res.data.data;
}

/** POST /api/v1/auth/phone/confirm/ — verify + attach the phone to the current user. */
export async function addPhoneConfirm(phone: string, code: string): Promise<{ phone: string }> {
  const res = await api.post<{ success: boolean; data: { phone: string } }>(
    "/auth/phone/confirm/", { phone, code }
  );
  return res.data.data;
}

/** POST /api/v1/auth/pin/set/ — set the PIN + trust this device (returns a device token). */
export async function pinSet(pin: string): Promise<{ pin_set: boolean; device_token: string; phone: string }> {
  const res = await api.post<{ success: boolean; data: { pin_set: boolean; device_token: string; phone: string } }>(
    "/auth/pin/set/", { pin }
  );
  return res.data.data;
}

/** POST /api/v1/auth/pin/verify/ — unlock on a trusted device (phone + pin + device token). */
export async function pinVerify(phone: string, pin: string, deviceToken: string): Promise<OtpSelectResult> {
  const res = await api.post<{ success: boolean; data: OtpSelectResult }>(
    "/auth/pin/verify/", { phone, pin, device_token: deviceToken }
  );
  return res.data.data;
}

// Trusted-device storage (set on PIN setup, read on the lock screen).
export const DEVICE_TOKEN_KEY = "step-device-token";
export const DEVICE_PHONE_KEY = "step-device-phone";

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface UpdateProfilePayload {
  first_name?: string;
  last_name?: string;
  phone?: string;
  language?: string;
  timezone?: string;
}

/** POST /api/v1/auth/login/ */
export async function login(payload: LoginPayload): Promise<LoginResponse> {
  const res = await api.post<{ success: boolean; data: LoginResponse }>("/auth/login/", payload);
  return res.data.data;
}

/** POST /api/v1/auth/logout/ */
export async function logout(refreshToken: string): Promise<void> {
  await api.post("/auth/logout/", { refresh_token: refreshToken });
}

// NOTE: A standalone refreshAccessToken() helper used to live here as a second,
// independent refresh path. It was unused and has been removed — token refresh
// is owned exclusively by the axios response interceptor (src/lib/axios.ts),
// which coordinates a cross-tab single-flight refresh. Reintroducing a separate
// refresher risks racing the interceptor.

/** GET /api/v1/auth/profile/ */
export async function getProfile(): Promise<AuthUser> {
  const res = await api.get<{ success: boolean; data: AuthUser }>("/auth/profile/");
  return res.data.data;
}

/** PATCH /api/v1/auth/profile/ */
export async function updateProfile(payload: UpdateProfilePayload): Promise<AuthUser> {
  const res = await api.patch<{ success: boolean; data: AuthUser }>("/auth/profile/", payload);
  return res.data.data;
}

/** POST /api/v1/auth/change-password/ */
export async function changePassword(payload: ChangePasswordPayload): Promise<void> {
  await api.post("/auth/change-password/", payload);
}

export interface LoginEvent {
  id: string;
  ip_address: string | null;
  user_agent: string;
  success: boolean;
  failure_reason: string;
  created_at: string;
  // Enriched by backend view via parse_device()
  device_type?: "WEB" | "MOBILE" | "ELECTRON" | "CASHIER";
  device_name?: string;
  browser?: string;
  os?: string;
  is_mobile?: boolean;
}

/** GET /api/v1/auth/login-events/?limit=N */
export async function getLoginEvents(limit = 20): Promise<LoginEvent[]> {
  const res = await api.get<{ success: boolean; data: LoginEvent[] }>("/auth/login-events/", { params: { limit } });
  return res.data.data;
}
